/**
 * Sign Up Prompt Context
 *
 * Global context for managing the sign-in prompt modal across the app.
 * Used when anonymous users try to perform protected actions.
 */
"use client";

import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

import { usePathname } from "next/navigation";

import { SignInModal } from "@/components/shared/SignInModal";
import { setPendingAuthRedirect } from "@/lib/pending-auth-redirect";
import {
  BRAND_EXPLORER_PATH,
  CART_PATH,
  COLLECTIONS_PATH,
  LANDING_PAGE_PATH,
  PROFILE_PATH,
  isSafeReturnTo,
} from "@/lib/route-paths";

type SignUpAction = "cart" | "collection" | "brand" | "general" | "profile";

interface SignUpPromptContextType {
  showSignUpPrompt: (action?: SignUpAction) => void;
  hideSignUpPrompt: () => void;
  isOpen: boolean;
}

const SignUpPromptContext = createContext<SignUpPromptContextType | undefined>(undefined);

export function SignUpPromptProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState<SignUpAction>("general");

  const showSignUpPrompt = (promptAction: SignUpAction = "general") => {
    if (promptAction !== "profile") {
      setPendingAuthRedirect(null);
    }
    setAction(promptAction);
    setIsOpen(true);
  };

  const hideSignUpPrompt = () => {
    setIsOpen(false);
  };

  // Allow other parts of the app (e.g. onboarding) to open this modal without a direct hook dependency.
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<{ action?: SignUpAction }>;
      const nextAction = customEvent.detail?.action ?? "general";
      showSignUpPrompt(nextAction);
    };
    window.addEventListener("signUpPrompt:show", handler);
    return () => window.removeEventListener("signUpPrompt:show", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const signInAction = url.searchParams.get("signIn");
    if (signInAction !== "profile") return;

    const returnTo = url.searchParams.get("returnTo");
    setPendingAuthRedirect(isSafeReturnTo(returnTo) ? returnTo : null);
    showSignUpPrompt("profile");

    url.searchParams.delete("signIn");
    url.searchParams.delete("returnTo");
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, "", nextUrl);
  }, [pathname]);

  // Configure modal props based on action
  const getModalProps = () => {
    switch (action) {
      case "collection":
        return {
          title: "Log in to save items",
          subtitle:
            "Connect your Cosimo account to save your favorite items and build collections.",
          redirectPath: COLLECTIONS_PATH,
        };
      case "cart":
        return {
          title: "Log in to add items to cart",
          subtitle: "Connect your Cosimo account to add items to your cart and check out.",
          redirectPath: CART_PATH,
        };
      case "brand":
        return {
          title: "Log in to save brands",
          subtitle:
            "Connect your Cosimo account to save your favorite brands and get personalized recommendations.",
          redirectPath: BRAND_EXPLORER_PATH,
        };
      case "profile":
        return {
          title: "Log in to view your profile",
          subtitle: "Connect your Cosimo account to view account details and style preferences.",
          redirectPath: PROFILE_PATH,
        };
      default:
        return {
          title: "Sign In",
          redirectPath: LANDING_PAGE_PATH,
        };
    }
  };

  const modalProps = getModalProps();

  return (
    <SignUpPromptContext.Provider value={{ showSignUpPrompt, hideSignUpPrompt, isOpen }}>
      {children}
      <SignInModal isOpen={isOpen} onClose={hideSignUpPrompt} action={action} {...modalProps} />
    </SignUpPromptContext.Provider>
  );
}

export function useSignUpPrompt() {
  const context = useContext(SignUpPromptContext);
  if (!context) {
    throw new Error("useSignUpPrompt must be used within SignUpPromptProvider");
  }
  return context;
}
